import * as mutationTypes from "../store/mutation-types";
import userNotificationsHelper from "./userNotificationsHelper";
import ProductSelection from "../domains2/orders/ProductSelection";
import {generateProductCatalog} from "./ProductCatalogHelpers";
import { DeliveryMethodsEnum } from "../domains2/libraries/DeliveryMethods";
import routeMapper from "../router/routeMapper";
import { pushGTMInternal } from "../integrations/tracking/gtm/pushGtmEvents";

const isCatalogDefined = (store) => ((store.getters.productCatalog || {}).products || []).length;
const isShopDefined = (store) => (store.getters.globalSelectedShop || {}).id;
const isDeliveryChoiceDefined = (store) => (store.getters.tenantSpecificData || {}).deliveryChoice;
const isSameShop = (store, shopId) => shopId === (store.getters.globalSelectedShop || {}).id;
const isSameDeliveryChoice = (store, deliveryChoice) => deliveryChoice === (store.getters.tenantSpecificData || {}).deliveryChoice;
const parseMaybeJson = (value) => {
	if (typeof value !== "string") {
		return value || {};
	}

	try {
		return JSON.parse(value);
	} catch (error) {
		return {};
	}
};

const getNotificationData = (payload = {}) => {
	const notificationData = ((payload.notification || {}).data || {});
	if (Object.keys(notificationData).length) {
		return notificationData;
	}

	if (payload.values && typeof payload.values === "object") {
		return payload.values;
	}

	if (payload.data && typeof payload.data === "object") {
		return payload.data;
	}

	return {};
};

const normalizeActionPayload = (payload = {}) => {
	if (((payload.notification || {}).data || null) !== null) {
		return payload;
	}

	const data = getNotificationData(payload);
	return {
		...payload,
		notification: {
			...(payload.notification || {}),
			data,
			title: (payload.notification || {}).title || data.title,
			subtitle: (payload.notification || {}).subtitle || data.subtitle,
			body: (payload.notification || {}).body || data.alert || data.body
		}
	};
};

const normalizeReceivedPayload = (payload = {}) => {
	if (payload.data && typeof payload.data === "object") {
		return payload;
	}

	if (payload.notification && (payload.notification.data || payload.notification.body || payload.notification.title)) {
		return payload.notification;
	}

	const data = getNotificationData(payload);
	return {
		...payload,
		data,
		title: payload.title || data.title,
		subtitle: payload.subtitle || data.subtitle,
		body: payload.body || data.alert || data.body
	};
};

const getRoutePath = (routeContext) => (
	((routeContext || {}).$route || {}).path
	|| ((routeContext || {}).currentRoute || {}).path
	|| (routeContext || {}).path
	|| ""
);

export const onPushNotificationActionPerformedCallback  = async (router, store, obj) => {
	const normalizedObj = normalizeActionPayload(obj || {});
	if (!normalizedObj.notification || !normalizedObj.notification.data) {
		return;
	}

	const {postOrderDiscount} = (normalizedObj.notification || {}).data || {};
	if (postOrderDiscount) {
		// handles PN sent by our backend system, after order is purchased - using firebase admin sdk
		const {shopId, deliveryChoice, productId, voucherCode, campaignName, trackOpened, discountedPrice} = parseMaybeJson(postOrderDiscount);
		if (!shopId || !Object.values(DeliveryMethodsEnum).includes(deliveryChoice) || !productId) {
			return;
		}

		if (trackOpened) {
			const path = getRoutePath(router);
			pushGTMInternal(
				"ppd_opened",
				path,
				path,
				"",
				{
					campaignName
				}
			);
		}
		const goToCheckout = (router, store, {postOrderDiscountFlowWithCatalogLoading} = {}) => {
			const enableThreeStepFullscreenCheckoutMobile = store.getters.featureToggles.enableThreeStepFullscreenCheckoutMobile;
			const cartRoute = enableThreeStepFullscreenCheckoutMobile ? routeMapper.routeNames.fullBasketCheckout : routeMapper.routeNames.checkoutCart;
			const activeStep = enableThreeStepFullscreenCheckoutMobile ? 2 : undefined;
			router.push({
				name: cartRoute,
				params: {
					postOrderDiscountFlowWithCatalogLoading,
					activeStep,
					voucherCode,
					...(trackOpened ? {
						trackPPDConverted: true,
						campaignName,
						discountedPrice
					}: {})
				}
			});
		};
		const addProductToCart = async () => {
			const ps = ProductSelection.createFromProduct(store.getters.productCatalog.getProduct(productId));
			await store.dispatch(mutationTypes.ADD_PRODUCT_SELECTION_TO_CART, {
				...ps,
				quantity: 1
			});
			return ps;
		};
		const addPpdProductSelectionToStore = (store, ps) => {
			const ppdProductSelectionIds = [...store.getters.ppdProductSelectionIds, ps.productSelectionId];
			store.commit(mutationTypes.POST_PURCHASE_DISCOUNT_PRODUCT_SELECTIONS, ppdProductSelectionIds);
		};
		if (isCatalogDefined(store) && isDeliveryChoiceDefined(store) && isShopDefined(store)) {
			if (isSameShop(store, shopId) && isSameDeliveryChoice(store, deliveryChoice)) {
				const ps = await addProductToCart();
				goToCheckout(router, store, {postOrderDiscountFlowWithCatalogLoading: false});
				addPpdProductSelectionToStore(store, ps);
				return;
			} else {
				// is custom logic needed when shop id or delivery choice differs? (shall we forget current cart selection and create new catalog in shop defined in PN, and reset the cart)
			}
		} else {
			store.commit(mutationTypes.SHOW_LOADER_AFTER_PPD_CATALOG_CREATION, true);
			await generateProductCatalog(shopId, deliveryChoice, store);
			const ps = await addProductToCart();
			goToCheckout(router, store, {postOrderDiscountFlowWithCatalogLoading: true});
			addPpdProductSelectionToStore(store, ps);
			store.commit(mutationTypes.SHOW_LOADER_AFTER_PPD_CATALOG_CREATION, false);
		}
	} else {
		// handles PN sent by Sunset - from firebase or however they do it
		userNotificationsHelper.createUserNotificationOnPushNotificationTap(router, normalizedObj);
	}
};

export const onPushNotificationReceivedCallback = (routeContext, notification) => {
	const normalizedNotification = normalizeReceivedPayload(notification || {});
	const {postOrderDiscount} = (normalizedNotification || {}).data || {};

	if (postOrderDiscount) {
		const {campaignName, trackReceived} = parseMaybeJson(postOrderDiscount);

		if (trackReceived) {
			const path = getRoutePath(routeContext);
			pushGTMInternal(
				"ppd_received",
				path,
				path,
				"",
				{
					campaignName
				}
			);
		}
	}
	userNotificationsHelper.createUserNotification(normalizedNotification);
};
